import {
  EventType,
  IBrand,
  type ICalendarEventSchedule,
  IEventable,
  isAppointment,
  isAppointmentRequest,
  isCalendarEvent,
  isPreBlockEvent,
  ITreatmentCategory,
  PreBlockEvent,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  IReffable,
  isSameRef,
  multiMap,
  snapshot,
  type Timestamp,
  toMomentTz,
} from '@principle-theorem/shared';
import { compact, first, uniqBy } from 'lodash';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { TimezoneResolver } from '../../timezone';
import { getStafferEventables$ } from '../event/brand-event-option-finder';
import { Event } from '../event/event';
import { EventTimePeriod } from '../event/event-time-period';
import { OrganisationCache } from '../organisation/organisation-cache';

export interface IDoubleBookingStats {
  overlapsBlockingEvent: boolean;
  overlappingEvents: IEventable[];
  conflictingPreBlocks: PreBlockEvent[];
  allowedTreatmentCategories: string[];
}

export interface IReplacedScheduleOccurrence {
  scheduleRef: DocumentReference<ICalendarEventSchedule>;
  date: Timestamp;
}

export class SchedulingScenario {
  static async getByDoubleBookingResults(
    brand: IReffable<IBrand>,
    currentEventable: IEventable,
    treatmentCategory?: DocumentReference<ITreatmentCategory>,
    replacedOccurrence?: IReplacedScheduleOccurrence
  ): Promise<IDoubleBookingStats> {
    const events = await SchedulingScenario.getOverlappingEvents(
      currentEventable,
      brand
    );
    const overlappingEvents = replacedOccurrence
      ? compact(
          await Promise.all(
            events.map(async (eventable) =>
              (await SchedulingScenario._isReplacedOccurrence(
                eventable,
                replacedOccurrence
              ))
                ? undefined
                : eventable
            )
          )
        )
      : events;

    const conflictingPreBlocks = SchedulingScenario.findConflictingPreBlocks(
      overlappingEvents,
      currentEventable,
      treatmentCategory
    );

    const allowedTreatmentCategories =
      await SchedulingScenario.getAllowedTreatmentCategories(
        conflictingPreBlocks
      );

    const overlapsBlockingEvent =
      SchedulingScenario.eventableOverlapsBlockingEvent(
        currentEventable,
        overlappingEvents
      );

    return {
      overlapsBlockingEvent,
      overlappingEvents,
      conflictingPreBlocks,
      allowedTreatmentCategories,
    };
  }

  static findConflictingPreBlocks(
    eventables: IEventable[],
    currentEventable: IEventable,
    treatmentCategory?: DocumentReference<ITreatmentCategory>
  ): PreBlockEvent[] {
    const filteredEventables = eventables.filter((eventable) => {
      const isSameEventable =
        eventable.ref &&
        currentEventable.ref &&
        isSameRef(eventable.ref, currentEventable.ref);

      if (isSameEventable || !isPreBlockEvent(eventable.event)) {
        return false;
      }
      return true;
    });

    const overlappingPreBlocks = EventTimePeriod.findOverlappingEventables(
      filteredEventables,
      Event.toTimePeriod(currentEventable.event)
    )
      .map((eventable) => eventable.event)
      .filter((event): event is PreBlockEvent => isPreBlockEvent(event));

    return overlappingPreBlocks.filter((preBlock) => {
      if (!preBlock.allowedTreatmentCategories.length) {
        return false;
      }

      if (!treatmentCategory) {
        return true;
      }

      return preBlock.allowedTreatmentCategories.every(
        (category) => !isSameRef(category, treatmentCategory)
      );
    });
  }

  static eventableOverlapsBlockingEvent(
    currentEventable: IEventable,
    eventables: IEventable[]
  ): boolean {
    return EventTimePeriod.hasOverlappingEventables(
      eventables.filter((eventable) => {
        const isRequest = isAppointmentRequest(eventable);
        const isSameEventable = isSameRef(eventable.ref, currentEventable.ref);
        const isRosterEvent = eventable.event.type === EventType.RosteredOn;
        const isBlockingEvent =
          isAppointment(eventable) ||
          (isCalendarEvent(eventable) && eventable.isBlocking);
        return (
          !isSameEventable && isBlockingEvent && !isRequest && !isRosterEvent
        );
      }),
      Event.toTimePeriod(currentEventable.event)
    );
  }

  static async getOverlappingEvents(
    currentEventable: IEventable,
    brand: IReffable<IBrand>
  ): Promise<IEventable[]> {
    if (currentEventable.event.type === EventType.RosteredOn) {
      return [];
    }

    const staffer = first(Event.staff(currentEventable.event));
    if (!staffer) {
      return [];
    }
    const timezone = await TimezoneResolver.fromEvent(currentEventable);
    const timePeriod = Event.toTimePeriod(currentEventable.event, timezone);
    const queryPeriod = {
      from: toMomentTz(timePeriod.from.clone(), timezone).startOf('day'),
      to: timePeriod.to.clone(),
    };

    const practice = currentEventable.event.practice;
    return snapshot(
      getStafferEventables$(queryPeriod, [staffer], practice, brand)
    );
  }

  static getAllowedTreatmentCategories(
    preBlocks: PreBlockEvent[]
  ): Promise<string[]> {
    const categories = uniqBy(
      preBlocks.map((preBlock) => preBlock.allowedTreatmentCategories).flat(),
      (categoryRef) => categoryRef.path
    );

    return snapshot(
      combineLatest(
        categories.map((category) =>
          OrganisationCache.treatmentCategories.get.getDoc(category)
        )
      ).pipe(
        map(compact),
        multiMap((category) => category.name),
        map((names) => names.sort())
      )
    );
  }

  private static async _isReplacedOccurrence(
    eventable: IEventable,
    replacedOccurrence: IReplacedScheduleOccurrence
  ): Promise<boolean> {
    if (
      !isCalendarEvent(eventable) ||
      !isSameRef(eventable.scheduleRef, replacedOccurrence.scheduleRef)
    ) {
      return false;
    }
    const timezone = await TimezoneResolver.fromEvent(eventable);
    return toMomentTz(eventable.event.from, timezone).isSame(
      toMomentTz(replacedOccurrence.date, timezone),
      'day'
    );
  }
}
